// Voice Preference Service - SIMPLIFIED FIX
// V8.0.2: Restored simple, working Telugu voice support

let cachedVoices = [];
let voicesLoaded = false;

const initVoices = () => {
  return new Promise((resolve) => {
    const loadVoices = () => {
      cachedVoices = window.speechSynthesis.getVoices();
      if (cachedVoices.length > 0) {
        voicesLoaded = true;
        console.log(`🔊 Loaded ${cachedVoices.length} voices`);
        resolve(cachedVoices);
      }
    };
    
    loadVoices();
    
    if ('speechSynthesis' in window) {
      window.speechSynthesis.onvoiceschanged = () => {
        loadVoices();
      };
    }
    
    setTimeout(() => {
      if (!voicesLoaded) {
        loadVoices();
        resolve(cachedVoices);
      }
    }, 500);
  });
};

if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
  initVoices();
}

export const VOICE_OPTIONS = {
  INDIAN_ENGLISH_FEMALE: 'indian_english_female',
  INDIAN_HINDI_FEMALE: 'indian_hindi_female',
  INDIAN_TELUGU_FEMALE: 'indian_telugu_female',
  INDIAN_TAMIL_FEMALE: 'indian_tamil_female',
  INDIAN_SANSKRIT_FEMALE: 'indian_sanskrit_female',
  INDIAN_KANNADA_FEMALE: 'indian_kannada_female',
  INDIAN_MALAYALAM_FEMALE: 'indian_malayalam_female',
  INDIAN_BENGALI_FEMALE: 'indian_bengali_female',
  INDIAN_MARATHI_FEMALE: 'indian_marathi_female',
  INDIAN_GUJARATI_FEMALE: 'indian_gujarati_female',
  INDIAN_PUNJABI_FEMALE: 'indian_punjabi_female',
  URDU_FEMALE: 'urdu_female',
};

export const VOICE_LABELS = {
  [VOICE_OPTIONS.INDIAN_ENGLISH_FEMALE]: '🇮🇳 Indian English (Female)',
  [VOICE_OPTIONS.INDIAN_HINDI_FEMALE]: '🇮🇳 Hindi (Female)',
  [VOICE_OPTIONS.INDIAN_TELUGU_FEMALE]: '🇮🇳 Telugu (Female)',
  [VOICE_OPTIONS.INDIAN_TAMIL_FEMALE]: '🇮🇳 Tamil (Female)',
  [VOICE_OPTIONS.INDIAN_SANSKRIT_FEMALE]: '🕉️ Sanskrit (Female)',
  [VOICE_OPTIONS.INDIAN_KANNADA_FEMALE]: '🇮🇳 Kannada (Female)',
  [VOICE_OPTIONS.INDIAN_MALAYALAM_FEMALE]: '🇮🇳 Malayalam (Female)',
  [VOICE_OPTIONS.INDIAN_BENGALI_FEMALE]: '🇮🇳 Bengali (Female)',
  [VOICE_OPTIONS.INDIAN_MARATHI_FEMALE]: '🇮🇳 Marathi (Female)',
  [VOICE_OPTIONS.INDIAN_GUJARATI_FEMALE]: '🇮🇳 Gujarati (Female)',
  [VOICE_OPTIONS.INDIAN_PUNJABI_FEMALE]: '🇮🇳 Punjabi (Female)',
  [VOICE_OPTIONS.URDU_FEMALE]: '🌐 Urdu (Female)',
};

// Map chat language code → best VOICE_OPTION for TTS
export const LANGUAGE_TO_VOICE = {
  'en-IN': VOICE_OPTIONS.INDIAN_ENGLISH_FEMALE,
  'hi-IN': VOICE_OPTIONS.INDIAN_HINDI_FEMALE,
  'te-IN': VOICE_OPTIONS.INDIAN_TELUGU_FEMALE,
  'ta-IN': VOICE_OPTIONS.INDIAN_TAMIL_FEMALE,
  'kn-IN': VOICE_OPTIONS.INDIAN_KANNADA_FEMALE,
  'ml-IN': VOICE_OPTIONS.INDIAN_MALAYALAM_FEMALE,
  'bn-IN': VOICE_OPTIONS.INDIAN_BENGALI_FEMALE,
  'mr-IN': VOICE_OPTIONS.INDIAN_MARATHI_FEMALE,
  'gu-IN': VOICE_OPTIONS.INDIAN_GUJARATI_FEMALE,
  'pa-IN': VOICE_OPTIONS.INDIAN_PUNJABI_FEMALE,
  'ur-PK': VOICE_OPTIONS.URDU_FEMALE,
  'sa-IN': VOICE_OPTIONS.INDIAN_SANSKRIT_FEMALE,
};

export const getVoicePreference = () => {
  const saved = localStorage.getItem('voice_preference');
  return saved || VOICE_OPTIONS.INDIAN_ENGLISH_FEMALE;
};

export const saveVoicePreference = (voiceOption) => {
  localStorage.setItem('voice_preference', voiceOption);
  console.log(`🔊 Voice preference saved: ${VOICE_LABELS[voiceOption]}`);
};

// V8.0.2: SIMPLIFIED - Just find the best Telugu/Hindi/Tamil voice
// V11.10.1: FIXED - Now actually uses voicePreference parameter
export const getBestVoice = (languageCode = 'en-IN', voicePreference = null) => {
  let voices = cachedVoices.length > 0 ? cachedVoices : window.speechSynthesis.getVoices();

  if (voices.length > 0 && cachedVoices.length === 0) {
    cachedVoices = voices;
    voicesLoaded = true;
  }

  if (voices.length === 0) {
    console.warn('⚠️ No voices available yet');
    window.speechSynthesis.getVoices();
    return null;
  }

  // V11.10.1: Load saved voice preference if not provided
  const actualPreference = voicePreference || getVoicePreference();
  console.log(`🔍 Finding voice for: ${languageCode}, preference: ${actualPreference}`);

  const langPrefix = languageCode.split('-')[0]; // 'te' from 'te-IN'

  // V11.10.1: Enhanced voice matching based on preference
  // V11.10.2: Removed male voices (not consistently available across browsers)
  const preferenceKeywords = {
    [VOICE_OPTIONS.INDIAN_ENGLISH_FEMALE]: {
      langs: ['en-IN', 'en-US', 'en-GB'],
      keywords: ['heera', 'raveena', 'neerja', 'veena', 'female', 'woman'],
      gender: 'female'
    },
    [VOICE_OPTIONS.INDIAN_HINDI_FEMALE]: {
      langs: ['hi-IN'],
      keywords: ['lekha', 'kalpana', 'swara', 'female', 'woman', 'हिंदी'],
      gender: 'female'
    },
    [VOICE_OPTIONS.INDIAN_TELUGU_FEMALE]: {
      langs: ['te-IN'],
      keywords: ['shruti', 'chitra', 'female', 'woman', 'telugu', 'తెలుగు'],
      gender: 'female'
    },
    [VOICE_OPTIONS.INDIAN_TAMIL_FEMALE]: {
      langs: ['ta-IN'],
      keywords: ['valluvar', 'female', 'woman', 'tamil', 'தமிழ்'],
      gender: 'female'
    },
    [VOICE_OPTIONS.INDIAN_SANSKRIT_FEMALE]: {
      langs: ['sa-IN', 'hi-IN'],
      keywords: ['sanskrit', 'संस्कृतम्', 'female', 'woman'],
      gender: 'female'
    },
    [VOICE_OPTIONS.INDIAN_KANNADA_FEMALE]: {
      langs: ['kn-IN'],
      keywords: ['kannada', 'ಕನ್ನಡ', 'female', 'woman'],
      gender: 'female'
    },
    [VOICE_OPTIONS.INDIAN_MALAYALAM_FEMALE]: {
      langs: ['ml-IN'],
      keywords: ['malayalam', 'മലയാളം', 'female', 'woman'],
      gender: 'female'
    },
    [VOICE_OPTIONS.INDIAN_BENGALI_FEMALE]: {
      langs: ['bn-IN', 'bn-BD'],
      keywords: ['bengali', 'বাংলা', 'female', 'woman'],
      gender: 'female'
    },
    [VOICE_OPTIONS.INDIAN_MARATHI_FEMALE]: {
      langs: ['mr-IN'],
      keywords: ['marathi', 'मराठी', 'female', 'woman'],
      gender: 'female'
    },
    [VOICE_OPTIONS.INDIAN_GUJARATI_FEMALE]: {
      langs: ['gu-IN'],
      keywords: ['gujarati', 'ગુજરાતી', 'female', 'woman'],
      gender: 'female'
    },
    [VOICE_OPTIONS.INDIAN_PUNJABI_FEMALE]: {
      langs: ['pa-IN', 'pa-PK'],
      keywords: ['punjabi', 'ਪੰਜਾਬੀ', 'female', 'woman'],
      gender: 'female'
    },
    [VOICE_OPTIONS.URDU_FEMALE]: {
      langs: ['ur-PK', 'ur-IN'],
      keywords: ['urdu', 'اردو', 'female', 'woman'],
      gender: 'female'
    },
  };

  // Try to match user's preference first
  if (actualPreference && preferenceKeywords[actualPreference]) {
    const pref = preferenceKeywords[actualPreference];

    // Try exact language match with preference keywords
    for (const keyword of pref.keywords) {
      const match = voices.find(v =>
        pref.langs.some(lang => v.lang.startsWith(lang)) &&
        v.name.toLowerCase().includes(keyword.toLowerCase())
      );
      if (match) {
        console.log(`✅ Preference match '${keyword}': ${match.name} (${match.lang})`);
        return match;
      }
    }

    // Try gender-based matching for the language
    const langMatch = voices.find(v =>
      pref.langs.some(lang => v.lang.startsWith(lang)) &&
      (v.name.toLowerCase().includes(pref.gender) ||
       (pref.gender === 'female' && v.name.toLowerCase().includes('woman')) ||
       (pref.gender === 'male' && v.name.toLowerCase().includes('man')))
    );
    if (langMatch) {
      console.log(`✅ Gender match: ${langMatch.name} (${langMatch.lang})`);
      return langMatch;
    }
  }

  // Define language-specific keywords for matching (fallback)
  const langKeywords = {
    'te': ['telugu', 'తెలుగు', 'shruti', 'chitra'],
    'hi': ['hindi', 'हिंदी', 'swara', 'hemant', 'lekha', 'kalpana'],
    'ta': ['tamil', 'தமிழ்', 'valluvar'],
    'kn': ['kannada', 'ಕನ್ನಡ'],
    'ml': ['malayalam', 'മലയാളം'],
    'bn': ['bengali', 'বাংলা'],
    'mr': ['marathi', 'मराठी'],
    'en': ['english', 'ravi', 'veena', 'neerja', 'heera', 'raveena'],
    'sa': ['sanskrit', 'संस्कृतम्', 'hindi']  // Sanskrit often uses Hindi voice as fallback
  };

  // Step 1: Try exact language code match (e.g., 'te-IN')
  let match = voices.find(v => v.lang === languageCode);
  if (match) {
    console.log(`✅ Exact match: ${match.name} (${match.lang})`);
    return match;
  }

  // Step 2: Try language prefix match (e.g., 'te' matches 'te-IN', 'te-US', etc.)
  match = voices.find(v => v.lang.startsWith(langPrefix + '-'));
  if (match) {
    console.log(`✅ Prefix match: ${match.name} (${match.lang})`);
    return match;
  }

  // Step 3: Search by voice name keywords
  const keywords = langKeywords[langPrefix] || [langPrefix];
  for (const keyword of keywords) {
    match = voices.find(v => 
      v.name.toLowerCase().includes(keyword.toLowerCase()) ||
      v.lang.toLowerCase().includes(keyword.toLowerCase())
    );
    if (match) {
      console.log(`✅ Keyword match '${keyword}': ${match.name} (${match.lang})`);
      return match;
    }
  }

  // Step 4: For English, prioritize high-quality natural voices
  // v11.12.26: Improved English voice selection to avoid robotic voices
  if (langPrefix === 'en') {
    // Get all English voices
    const englishVoices = voices.filter(v => v.lang.startsWith('en'));
    
    if (englishVoices.length > 0) {
      // Score voices based on quality indicators
      const scoredVoices = englishVoices.map(voice => {
        let score = 0;
        const nameLower = voice.name.toLowerCase();
        const langLower = voice.lang.toLowerCase();
        
        // High-quality indicators (premium voices)
        if (nameLower.includes('google')) score += 100;
        if (nameLower.includes('premium')) score += 90;
        if (nameLower.includes('enhanced')) score += 85;
        if (nameLower.includes('neural')) score += 80;
        if (nameLower.includes('natural')) score += 75;
        
        // Preferred regional voices
        if (langLower.includes('en-in') || nameLower.includes('india')) score += 50;
        if (nameLower.includes('heera') || nameLower.includes('raveena') || 
            nameLower.includes('neerja') || nameLower.includes('veena')) score += 60;
        
        // Good quality voices
        if (nameLower.includes('samantha') || nameLower.includes('karen') || 
            nameLower.includes('moira') || nameLower.includes('tessa')) score += 40;
        if (langLower.includes('en-gb') || langLower.includes('en-au')) score += 30;
        if (langLower.includes('en-us')) score += 25;
        
        // Female voices (generally more pleasant)
        if (nameLower.includes('female') || nameLower.includes('woman')) score += 20;
        
        // Avoid obviously low-quality/robotic voices
        if (nameLower.includes('default')) score -= 50;
        if (nameLower.includes('compact')) score -= 30;
        if (nameLower.includes('male') && !nameLower.includes('female')) score -= 10;
        
        return { voice, score };
      });
      
      // Sort by score (highest first) and pick the best
      scoredVoices.sort((a, b) => b.score - a.score);
      const bestVoice = scoredVoices[0].voice;
      
      console.log(`✅ Best English voice (score: ${scoredVoices[0].score}): ${bestVoice.name} (${bestVoice.lang})`);
      console.log(`🔍 Top 3 candidates:`, scoredVoices.slice(0, 3).map(v => `${v.voice.name} (${v.score})`));
      
      return bestVoice;
    }
  }

  // Step 5a: For Sanskrit, try Hindi as fallback (common on most platforms)
  if (langPrefix === 'sa') {
    console.log('⚠️ No Sanskrit voice found, trying Hindi as fallback...');
    const hindiVoice = voices.find(v =>
      v.lang.startsWith('hi-') ||
      v.name.toLowerCase().includes('hindi') ||
      v.name.toLowerCase().includes('lekha') ||
      v.name.toLowerCase().includes('swara')
    );
    if (hindiVoice) {
      console.log(`✅ Using Hindi voice as Sanskrit fallback: ${hindiVoice.name} (${hindiVoice.lang})`);
      return hindiVoice;
    }
  }

  // Step 5b: For Urdu, try Hindi as fallback (Hindustani — same spoken base, widely available)
  if (langPrefix === 'ur') {
    console.log('⚠️ No Urdu voice found, trying Hindi as fallback (Hindustani base)...');
    const hindiUrduFallback = voices.find(v =>
      v.lang.startsWith('hi-') ||
      v.name.toLowerCase().includes('hindi') ||
      v.name.toLowerCase().includes('lekha') ||
      v.name.toLowerCase().includes('swara') ||
      v.name.toLowerCase().includes('aditi')
    );
    if (hindiUrduFallback) {
      console.log(`✅ Using Hindi voice as Urdu fallback: ${hindiUrduFallback.name} (${hindiUrduFallback.lang})`);
      return hindiUrduFallback;
    }
  }

  // Step 6: Absolute fallback
  console.log(`⚠️ Using first available: ${voices[0].name} (${voices[0].lang})`);
  return voices[0];
};

export const getNaturalVoice = (languageCode = 'en-US') => {
  return getBestVoice(languageCode);
};

export const getAvailableVoices = () => {
  return cachedVoices.length > 0 ? cachedVoices : window.speechSynthesis.getVoices();
};

export const resetSpeechSynthesis = async () => {
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel();
    // Wait for cancel to fully complete (Chrome needs this)
    await new Promise(resolve => setTimeout(resolve, 150));
    
    // Clear any stuck state
    if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
    }
    
    // Final cancel to ensure clean state
    window.speechSynthesis.cancel();
  }
};

export const ensureVoicesLoaded = async () => {
  if (voicesLoaded && cachedVoices.length > 0) {
    return cachedVoices;
  }
  return initVoices();
};

// Safe speech wrapper that prevents "canceled" errors
export const safeSpeakWithRetry = async (utterance, maxRetries = 2) => {
  if (!('speechSynthesis' in window)) {
    throw new Error('Speech synthesis not supported');
  }

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      // Ensure clean state
      await resetSpeechSynthesis();
      
      return await new Promise((resolve, reject) => {
        let hasStarted = false;
        let hasEnded = false;
        
        utterance.onstart = () => {
          hasStarted = true;
          console.log(`✅ Speech started (attempt ${attempt + 1})`);
        };
        
        utterance.onend = () => {
          hasEnded = true;
          console.log(`✅ Speech ended successfully`);
          resolve({ success: true });
        };
        
        utterance.onerror = (event) => {
          console.error(`❌ Speech error: ${event.error} (attempt ${attempt + 1})`);
          
          // If it's a "canceled" error and we haven't started, retry
          if (event.error === 'canceled' && !hasStarted && attempt < maxRetries) {
            console.log(`🔄 Retrying due to canceled error...`);
            reject({ retry: true, error: event.error });
          } else {
            reject({ retry: false, error: event.error });
          }
        };
        
        // Speak
        window.speechSynthesis.speak(utterance);
        
        // Safety timeout
        setTimeout(() => {
          if (!hasStarted && !hasEnded) {
            console.warn('⚠️ Speech timeout - may have been blocked');
            reject({ retry: attempt < maxRetries, error: 'timeout' });
          }
        }, 1000);
      });
    } catch (error) {
      if (error.retry && attempt < maxRetries) {
        console.log(`🔄 Retrying speech (attempt ${attempt + 2}/${maxRetries + 1})...`);
        await new Promise(resolve => setTimeout(resolve, 200));
        continue;
      }
      throw error;
    }
  }
  
  throw new Error('Speech failed after retries');
};

// v11.12.31: Speak text using the user's voice preference
export const speakText = async (text, voiceOption = null) => {
  if (!('speechSynthesis' in window)) {
    console.error('Speech synthesis not supported');
    return { success: false, error: 'Speech synthesis not supported' };
  }

  try {
    // Stop any currently playing speech
    stopSpeaking();

    // Determine correct language code based on voice option
    let languageCode = 'en-IN';
    if (voiceOption) {
      if (voiceOption.includes('telugu')) {
        languageCode = 'te-IN';
      } else if (voiceOption.includes('tamil')) {
        languageCode = 'ta-IN';
      } else if (voiceOption.includes('hindi')) {
        languageCode = 'hi-IN';
      } else if (voiceOption.includes('sanskrit')) {
        languageCode = 'sa-IN';
      } else if (voiceOption.includes('kannada')) {
        languageCode = 'kn-IN';
      } else if (voiceOption.includes('malayalam')) {
        languageCode = 'ml-IN';
      } else if (voiceOption.includes('bengali')) {
        languageCode = 'bn-IN';
      } else if (voiceOption.includes('marathi')) {
        languageCode = 'mr-IN';
      }
    }

    const utterance = new SpeechSynthesisUtterance(text);
    const voice = getBestVoice(languageCode, voiceOption);

    if (voice) {
      utterance.voice = voice;
      utterance.lang = voice.lang;
    } else {
      utterance.lang = languageCode;
    }

    utterance.rate = 0.95;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    window.speechSynthesis.speak(utterance);
    return { success: true, voice: voice?.name };
  } catch (error) {
    console.error('Error speaking text:', error);
    return { success: false, error: error.message };
  }
};

// v11.12.31: Stop any currently playing speech
export const stopSpeaking = () => {
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel();
  }
};

export const testVoice = async (voiceOption, text = "Hello! This is how I sound.") => {
  if (!('speechSynthesis' in window)) {
    return { success: false, error: 'Speech synthesis not supported' };
  }

  try {
    await resetSpeechSynthesis();

    // Determine correct language code based on voice option
    let languageCode = 'en-IN';
    if (voiceOption.includes('telugu')) {
      languageCode = 'te-IN';
    } else if (voiceOption.includes('tamil')) {
      languageCode = 'ta-IN';
    } else if (voiceOption.includes('hindi')) {
      languageCode = 'hi-IN';
    } else if (voiceOption.includes('kannada')) {
      languageCode = 'kn-IN';
    } else if (voiceOption.includes('malayalam')) {
      languageCode = 'ml-IN';
    } else if (voiceOption.includes('bengali')) {
      languageCode = 'bn-IN';
    } else if (voiceOption.includes('marathi')) {
      languageCode = 'mr-IN';
    }

    const utterance = new SpeechSynthesisUtterance(text);
    const voice = getBestVoice(languageCode, voiceOption);

    if (!voice) {
      return { success: false, error: 'No suitable voice found' };
    }

    utterance.voice = voice;
    utterance.lang = voice.lang;
    utterance.rate = 0.95;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    console.log(`🔊 Testing voice: ${voice.name} (${voice.lang}) with text: "${text.substring(0, 50)}"`);

    await safeSpeakWithRetry(utterance);
    return { success: true, voice: voice.name };
  } catch (error) {
    return { success: false, error: error.error || error.message };
  }
};

export const runAudioDiagnostics = async () => {
  const results = {
    speechSynthesisSupported: false,
    voicesAvailable: 0,
    voicesList: [],
    testResult: null,
    browserInfo: navigator.userAgent
  };
  
  if ('speechSynthesis' in window) {
    results.speechSynthesisSupported = true;
    await ensureVoicesLoaded();
    
    const voices = getAvailableVoices();
    results.voicesAvailable = voices.length;
    results.voicesList = voices.map(v => ({
      name: v.name,
      lang: v.lang,
      local: v.localService
    }));
    
    return new Promise((resolve) => {
      const testUtterance = new SpeechSynthesisUtterance("Audio test.");
      testUtterance.volume = 1.0;
      testUtterance.rate = 1.0;
      testUtterance.pitch = 1.0;
      
      if (voices.length > 0) {
        testUtterance.voice = voices[0];
        testUtterance.lang = voices[0].lang;
      }
      
      testUtterance.onstart = () => {
        results.testResult = 'started';
      };
      
      testUtterance.onend = () => {
        results.testResult = 'completed';
        resolve(results);
      };
      
      testUtterance.onerror = (event) => {
        results.testResult = `error: ${event.error}`;
        resolve(results);
      };
      
      resetSpeechSynthesis();
      window.speechSynthesis.speak(testUtterance);
      
      setTimeout(() => {
        if (results.testResult === 'started') {
          results.testResult = 'timeout';
        }
        resolve(results);
      }, 5000);
    });
  }
  
  return results;
};

export default {
  VOICE_OPTIONS,
  VOICE_LABELS,
  getVoicePreference,
  saveVoicePreference,
  getBestVoice,
  getNaturalVoice,
  getAvailableVoices,
  testVoice,
  resetSpeechSynthesis,
  ensureVoicesLoaded,
  runAudioDiagnostics,
  safeSpeakWithRetry
};

